'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { supabase } from '@/lib/supabase'
import { Navbar } from '@/components/custom/main/navbar'
import { SiteFooter } from '@/components/custom/main/footer'

export function SignInPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const from = searchParams.get('from') ?? '/'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const { error: authError } = await supabase.auth.signInWithPassword({ email, password })

    if (authError) {
      if (authError.message.toLowerCase().includes('email not confirmed')) {
        setError('Please confirm your email before signing in. Check your inbox for the confirmation link.')
      } else if (authError.message.toLowerCase().includes('invalid login credentials')) {
        setError('Incorrect email or password.')
      } else {
        setError(authError.message)
      }
      setLoading(false)
      return
    }

    router.replace(from)
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#000000' }}>
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 pt-20">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <h1 className="text-base font-semibold text-white">Sign in</h1>
            <p className="mt-1.5 text-sm text-white/40">Welcome back</p>
          </div>

          <div
            className="rounded-lg border border-white/8 px-6 py-7"
            style={{ background: '#0a0c0e' }}
          >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-xs font-medium text-white/50 uppercase tracking-wider">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:border-white/25 focus:bg-white/8 transition-colors"
                  placeholder="you@example.com"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-xs font-medium text-white/50 uppercase tracking-wider">
                    Password
                  </label>
                  <Link href="/forgot-password" className="text-xs text-white/40 hover:text-white/70 transition-colors no-underline">
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:border-white/25 focus:bg-white/8 transition-colors"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="rounded-md border border-red-500/20 bg-red-500/8 px-3 py-2.5">
                  <p className="text-xs text-red-400 leading-relaxed">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-1 w-full rounded-md bg-white py-2.5 text-sm font-semibold text-black transition-opacity disabled:opacity-50 hover:opacity-90"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>

          <p className="mt-5 text-center text-sm text-white/35">
            Don't have an account?{' '}
            <Link href="/signup" className="text-white/70 hover:text-white transition-colors no-underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
